// Upload Wizard - Main Component
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Check, Loader } from 'lucide-react';
import { useUploadWizard } from './hooks/useUploadWizard';
import { UploadWizardProvider, useUploadWizardContext } from './context/UploadWizardContext';
import {
  Step1FileSelection,
  Step2Grouping,
  Step2FileTypeStatus,
  Step3Metadata,
  Step4Review,
} from './steps';
import { cn } from '@utils/classNames';
import { useFileStore } from '@stores/useFileStore';
import { uploadQueue } from '@utils/uploadQueue';
import { areAllFilesGrouped, groupLocalFiles } from './utils/groupLocalFiles';
import type { UploadFileGroup } from './types';

interface UploadWizardState {
  files: File[];
  groups?: UploadFileGroup[];
  currentStep: string;
  fileType: string;
  fileStatus: string;
  beatstarsSellUrl?: string;
  beatstarsPrice?: string;
  collaborator?: string;
}

interface UploadWizardProps {
  onUploadComplete?: () => void;
  onCancel?: (state?: UploadWizardState) => void;
  initialFiles?: File[];
  restoredState?: Partial<UploadWizardState>;
}

const UploadWizardContent: React.FC<UploadWizardProps> = ({
  onUploadComplete,
  onCancel,
  initialFiles,
  restoredState,
}) => {
  const wizard = useUploadWizardContext();
  const [uploadHandler, setUploadHandler] = useState<(() => void) | null>(null);
  const { uploadProgress, deleteFile, files } = useFileStore();
  const uploadedFileIdsRef = useRef<Set<string>>(new Set());
  const uploadCompletedRef = useRef(false);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isUploadActive = useMemo(() => {
    const fileNames = new Set(wizard.data.files.map((f) => f.name));
    return Array.from(uploadProgress.values()).some(
      (p) =>
        fileNames.has(p.fileName) &&
        (p.status === 'uploading' || p.status === 'pending' || p.status === 'retrying')
    );
  }, [uploadProgress, wizard.data.files]);

  useEffect(() => {
    (window as any).__uploadWizardOpen = true;
    return () => {
      (window as any).__uploadWizardOpen = false;
    };
  }, []);

  useEffect(() => {
    const existingTimeout = (window as any).__uploadWizardResetTimeout;
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      (window as any).__uploadWizardResetTimeout = null;
      resetTimeoutRef.current = null;
    }
  }, [restoredState, initialFiles]);

  useEffect(() => {
    if (restoredState && restoredState.files && restoredState.files.length > 0) {
      wizard.setData({
        files: restoredState.files,
        groups: restoredState.groups ?? groupLocalFiles(restoredState.files),
        fileType: restoredState.fileType as any,
        fileStatus: restoredState.fileStatus as any,
        beatstarsSellUrl: restoredState.beatstarsSellUrl ?? '',
        beatstarsPrice: restoredState.beatstarsPrice ?? '',
        collaborator: restoredState.collaborator ?? '',
      });

      if (restoredState.currentStep) {
        setTimeout(() => {
          wizard.goToStep(restoredState.currentStep as any);
        }, 0);
      }
    } else if (initialFiles && initialFiles.length > 0) {
      const audioFiles = initialFiles.filter((file) => file.type.startsWith('audio/'));
      if (audioFiles.length > 0) {
        wizard.setData({
          files: audioFiles,
          groups: groupLocalFiles(audioFiles),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredState?.files?.length, initialFiles?.length]);

  const cleanupUploadedFiles = useCallback(async () => {
    if (uploadCompletedRef.current) return;

    const fileNames = wizard.data.files.map((f) => f.name);
    const fileNamesSet = new Set(fileNames);
    const fileIdsToDelete = new Set<string>();

    uploadedFileIdsRef.current.forEach((id) => fileIdsToDelete.add(id));

    Array.from(uploadProgress.values()).forEach((progress) => {
      if (fileNamesSet.has(progress.fileName)) {
        fileIdsToDelete.add(progress.fileId);
      }
    });

    files.forEach((file) => {
      if (fileNamesSet.has(file.name) || fileNamesSet.has(file.originalName)) {
        fileIdsToDelete.add(file.id);
      }
    });

    const fileIdsArray = Array.from(fileIdsToDelete);
    if (fileIdsArray.length === 0) return;

    await Promise.all(
      fileIdsArray.map(async (fileId) => {
        try {
          await deleteFile(fileId);
        } catch (error) {
          console.error('Failed to delete file:', fileId, error);
        }
      })
    );

    uploadedFileIdsRef.current.clear();
  }, [deleteFile, wizard.data.files, files, uploadProgress]);

  useEffect(() => {
    (window as any).__uploadWizardState = () => ({
      files: wizard.data.files,
      groups: wizard.data.groups,
      currentStep: wizard.currentStep,
      fileType: wizard.data.fileType,
      fileStatus: wizard.data.fileStatus,
      beatstarsSellUrl: wizard.data.beatstarsSellUrl,
      beatstarsPrice: wizard.data.beatstarsPrice,
      collaborator: wizard.data.collaborator,
    });

    (window as any).__uploadWizardMarkComplete = () => {
      uploadCompletedRef.current = true;
    };

    (window as any).__uploadWizardCleanup = async () => {
      await cleanupUploadedFiles();
    };
  }, [wizard.data, wizard.currentStep, cleanupUploadedFiles]);

  useEffect(() => {
    const fileNamesSet = new Set(wizard.data.files.map((f) => f.name));

    Array.from(uploadProgress.values()).forEach((progress) => {
      if (fileNamesSet.has(progress.fileName)) {
        uploadedFileIdsRef.current.add(progress.fileId);
      }
    });

    files.forEach((file) => {
      if (fileNamesSet.has(file.name) || fileNamesSet.has(file.originalName)) {
        uploadedFileIdsRef.current.add(file.id);
      }
    });
  }, [uploadProgress, wizard.data.files, files]);

  const steps = [
    { id: 'files' as const, label: 'Files', mobileLabel: 'Files', component: Step1FileSelection },
    { id: 'grouping' as const, label: 'Grouping', mobileLabel: 'Group', component: Step2Grouping },
    { id: 'type' as const, label: 'Type & Status', mobileLabel: 'Type', component: Step2FileTypeStatus },
    { id: 'metadata' as const, label: 'Metadata', mobileLabel: 'Meta', component: Step3Metadata },
    { id: 'review' as const, label: 'Review', mobileLabel: 'Review', component: Step4Review },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === wizard.currentStep);
  const CurrentStepComponent = steps[currentStepIndex]?.component;

  const canProceed = useMemo(() => {
    if (wizard.currentStep === 'files') {
      return wizard.data.files.length > 0;
    }
    if (wizard.currentStep === 'grouping') {
      return areAllFilesGrouped(wizard.data.files, wizard.data.groups);
    }
    return true;
  }, [wizard.currentStep, wizard.data.files, wizard.data.groups]);

  return (
    <div className="w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl mx-auto flex flex-col min-h-0 h-full items-center justify-start">
      <div className="mb-2 sm:mb-2.5 md:mb-2 lg:mb-2.5 px-2 sm:px-0 flex-shrink-0 w-full">
        <div className="flex items-center w-full gap-1 sm:gap-1.5 md:gap-1 lg:gap-1">
          {steps.map((step, index) => {
            const isActive = step.id === wizard.currentStep;
            const isCompleted = index < currentStepIndex;
            const isClickable = isCompleted || index === currentStepIndex;

            return (
              <div key={step.id} className="flex items-center flex-1 min-w-0">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isClickable) {
                      wizard.goToStep(step.id);
                    }
                  }}
                  disabled={!isClickable}
                  className={cn(
                    'flex flex-col items-center w-full transition-all',
                    isClickable && 'cursor-pointer hover:scale-105 active:scale-95'
                  )}
                >
                  <div
                    className={cn(
                      'w-6 h-6 sm:w-7 sm:h-7 md:w-5 md:h-5 lg:w-6 lg:h-6 xl:w-7 xl:h-7 rounded-full flex items-center justify-center font-inter font-bold text-[10px] sm:text-xs md:text-[10px] lg:text-xs xl:text-sm transition-all mb-0.5 sm:mb-0 md:mb-0 shadow-lg flex-shrink-0',
                      isActive && 'bg-primary text-black shadow-primary/40',
                      isCompleted && 'bg-primary/90 text-black shadow-primary/20',
                      !isActive && !isCompleted && 'bg-white/10 text-white/50 border-2 border-dashed border-white/20',
                      isClickable && !isActive && 'hover:bg-white/20 hover:text-white hover:border-white/40'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-[9px] xs:text-[10px] sm:text-xs md:text-[10px] lg:text-[11px] xl:text-xs font-inter font-medium transition-colors text-center truncate w-full px-0',
                      isActive ? 'text-white font-semibold' : 'text-white/50',
                      isClickable && 'hover:text-white'
                    )}
                  >
                    <span className="sm:hidden">{step.mobileLabel}</span>
                    <span className="hidden sm:inline">{step.label}</span>
                  </span>
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-0 sm:mx-0.5 md:mx-1 lg:mx-1 xl:mx-1.5 transition-all border-t-2 flex-shrink',
                      isCompleted ? 'border-primary border-dashed' : 'border-white/20 border-dashed'
                    )}
                    style={{ minWidth: '4px' }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden w-full">
        <div className="bg-dark-secondary/60 backdrop-blur-xl border border-white/10 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-2xl shadow-black/20 flex flex-col relative overflow-hidden mb-16 sm:mb-16 md:mb-14 lg:mb-16 xl:mb-20 h-full w-full">
          <div className="min-h-0 h-full overflow-y-auto overflow-x-hidden px-3 pt-3 sm:px-4 sm:pt-4 md:px-2.5 md:pt-2.5 lg:px-3 lg:pt-3 xl:px-3.5 xl:pt-3.5 pb-[calc(4.5rem+env(safe-area-inset-bottom))] sm:pb-14 md:pb-12 lg:pb-14">
            {CurrentStepComponent &&
              (wizard.currentStep === 'review' ? (
                <CurrentStepComponent
                  onUploadComplete={onUploadComplete}
                  onUploadHandlerSet={(handler) => setUploadHandler(() => handler)}
                />
              ) : (
                <CurrentStepComponent />
              ))}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-dark-secondary/98 backdrop-blur-xl border-t border-white/10 z-[10001] px-3 sm:px-4 md:px-2.5 lg:px-3 xl:px-3.5 py-2 sm:py-2.5 md:py-2 lg:py-2 xl:py-2.5 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-2xl flex-shrink-0">
        <div className="max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl mx-auto flex items-center justify-between gap-2 sm:gap-2.5 md:gap-2 lg:gap-2">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
            {wizard.currentStep !== 'files' && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  wizard.prevStep();
                }}
                className="px-4 sm:px-5 md:px-3 lg:px-3.5 xl:px-4 py-2 sm:py-2.5 md:py-2 lg:py-2 rounded-lg lg:rounded-xl font-inter font-semibold text-sm sm:text-base md:text-xs lg:text-sm xl:text-base bg-white/10 text-white hover:bg-white/20 active:bg-white/15 transition-all touch-manipulation min-h-[44px] sm:min-h-[48px] md:min-h-[40px] lg:min-h-[44px] flex items-center justify-center z-[10002] relative"
              >
                <span className="hidden sm:inline">Back</span>
                <span className="sm:hidden">←</span>
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const fileNames = wizard.data.files.map((f) => f.name);
                uploadQueue.cancelByFileNames(fileNames);

                const stateGetter = (window as any).__uploadWizardState;
                if (stateGetter) {
                  onCancel?.(stateGetter());
                } else {
                  onCancel?.();
                }

                if (wizard.currentStep === 'review' && isUploadActive) {
                  cleanupUploadedFiles().catch((err) => {
                    console.error('Background cleanup failed:', err);
                  });
                }

                const RESET_DELAY_MS = 10000;
                if (resetTimeoutRef.current) {
                  clearTimeout(resetTimeoutRef.current);
                }
                resetTimeoutRef.current = setTimeout(() => {
                  wizard.reset();
                  uploadedFileIdsRef.current.clear();
                  resetTimeoutRef.current = null;
                  if ((window as any).__uploadWizardClearState) {
                    (window as any).__uploadWizardClearState();
                  }
                }, RESET_DELAY_MS);
                (window as any).__uploadWizardResetTimeout = resetTimeoutRef.current;
              }}
              className="px-3 sm:px-4 md:px-2.5 lg:px-3 py-2 sm:py-2 md:py-1.5 lg:py-1.5 rounded-lg lg:rounded-xl font-inter font-medium text-sm sm:text-base md:text-[11px] lg:text-xs text-white/60 hover:text-white hover:bg-white/10 active:bg-white/15 transition-all touch-manipulation min-h-[44px] sm:min-h-[48px] md:min-h-[36px] lg:min-h-[40px] flex items-center justify-center z-[10002] relative"
            >
              Cancel
            </button>
          </div>

          {wizard.currentStep === 'review' ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (uploadHandler && !isUploadActive) {
                  uploadHandler();
                }
              }}
              disabled={!uploadHandler || wizard.data.files.length === 0 || isUploadActive}
              className={cn(
                'ml-auto px-4 sm:px-5 md:px-3 lg:px-3.5 xl:px-4 py-2 sm:py-2.5 md:py-2 lg:py-2 rounded-lg lg:rounded-xl font-inter font-semibold text-sm sm:text-base md:text-xs lg:text-sm xl:text-base transition-all touch-manipulation min-h-[44px] sm:min-h-[48px] md:min-h-[40px] lg:min-h-[44px] flex items-center justify-center gap-2 sm:gap-2 md:gap-1.5 z-[10002] relative flex-shrink-0',
                !uploadHandler || wizard.data.files.length === 0 || isUploadActive
                  ? 'bg-white/10 text-white/50 cursor-not-allowed opacity-50'
                  : 'bg-gradient-to-r from-[#30d870] to-[#00ff61] text-black hover:scale-105 active:scale-95 shadow-lg shadow-primary/25'
              )}
            >
              {isUploadActive ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Uploading...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Confirm & Upload</span>
                  <span className="sm:hidden">Upload</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (canProceed) {
                  wizard.nextStep();
                }
              }}
              disabled={!canProceed}
              className={cn(
                'ml-auto px-2 sm:px-2.5 md:px-3 lg:px-3.5 xl:px-4 py-1 sm:py-1.5 md:py-2 lg:py-2 rounded-lg lg:rounded-xl font-inter font-semibold text-[10px] sm:text-[11px] md:text-xs lg:text-sm xl:text-base transition-all touch-manipulation min-h-[32px] sm:min-h-[36px] md:min-h-[40px] lg:min-h-[44px] flex items-center justify-center z-[10002] relative flex-shrink-0',
                !canProceed
                  ? 'bg-white/10 text-white/50 cursor-not-allowed opacity-50'
                  : 'bg-primary text-black hover:bg-primary/90 active:bg-primary/80 cursor-pointer'
              )}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const UploadWizard: React.FC<UploadWizardProps> = (props) => {
  const wizard = useUploadWizard();

  useEffect(() => {
    return () => {
      const existingTimeout = (window as any).__uploadWizardResetTimeout;
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        (window as any).__uploadWizardResetTimeout = null;
      }
    };
  }, []);

  return (
    <UploadWizardProvider value={wizard}>
      <UploadWizardContent {...props} />
    </UploadWizardProvider>
  );
};
